/**
 * AdminTrashPage — Papelera del admin.
 *
 * Lista los elementos borrados (noticias, eventos, páginas) con tabs por tipo.
 * Permite restaurar individualmente o vaciar permanentemente. Los elementos
 * se purgan automáticamente al cabo de 90 días (lazy purge en el backend).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, RotateCcw, AlertTriangle, Loader2, ArrowLeft, FileText, Calendar, BookOpen, Search, Users } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { toast } from 'sonner';

const COLLECTION_META = {
  news:      { label: 'Noticias', icon: BookOpen, colorClasses: 'bg-blue-50 text-blue-600',       route: '/admin/news' },
  events:    { label: 'Eventos',  icon: Calendar, colorClasses: 'bg-rose-50 text-rose-600',       route: '/admin/events' },
  cms_pages: { label: 'Páginas',  icon: FileText, colorClasses: 'bg-emerald-50 text-emerald-600', route: '/admin/pages' },
  interpreters: { label: 'Intérpretes', icon: Users, colorClasses: 'bg-cmic-50 text-cmic-600', route: '/admin/interpreters' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'hace un momento';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

function daysUntilPurge(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = (d.getTime() - Date.now()) / (1000 * 86400);
  return Math.max(0, Math.ceil(diff));
}

export default function AdminTrashPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], counts: {}, retention_days: 90, collections: {} });
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data: res } = await api.get('/admin/trash');
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar la papelera');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function restoreItem(id, title) {
    if (!window.confirm(`¿Restaurar "${title}"? Volverá a su sección original.`)) return;
    setBusyId(id);
    try {
      await api.post(`/admin/trash/${id}/restore`);
      toast.success('Restaurado');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo restaurar');
    } finally {
      setBusyId(null);
    }
  }

  async function purgeItem(id, title) {
    if (!window.confirm(`¿Eliminar PERMANENTEMENTE "${title}"?\n\nEsta acción no se puede deshacer.`)) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/trash/${id}`);
      toast.success('Eliminado permanentemente');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo eliminar');
    } finally {
      setBusyId(null);
    }
  }

  async function emptyAll(collection) {
    const label = collection ? COLLECTION_META[collection]?.label : 'TODA la papelera';
    if (!window.confirm(`¿Vaciar ${label}?\n\nSe perderán PERMANENTEMENTE todos los elementos. Esta acción no se puede deshacer.`)) return;
    if (!window.confirm('Última confirmación: ¿estás completamente seguro?')) return;
    try {
      const params = collection ? `?collection=${collection}` : '';
      const { data: res } = await api.delete(`/admin/trash${params}`);
      toast.success(`${res.deleted} elemento(s) eliminado(s) permanentemente`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo vaciar la papelera');
    }
  }

  const filteredItems = (data.items || []).filter((it) => {
    if (activeTab !== 'all' && it.collection !== activeTab) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (it.title || '').toLowerCase().includes(q);
    }
    return true;
  });

  const totalCount = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-trash-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-cmic-600 mb-2" data-testid="trash-back-link">
            <ArrowLeft size={14} /> Volver al panel
          </Link>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <Trash2 size={24} className="text-rose-500" /> Papelera
          </h1>
          <p className="text-sm text-charcoal-500 mt-1">
            Los elementos eliminados se conservan {data.retention_days || 90} días antes de ser borrados permanentemente.
          </p>
        </div>
        {totalCount > 0 && (
          <button
            onClick={() => emptyAll(null)}
            data-testid="trash-empty-all-btn"
            className="inline-flex items-center gap-2 text-sm font-medium text-rose-600 border border-rose-200 px-3 py-2 rounded-lg hover:bg-rose-50"
          >
            <Trash2 size={14} /> Vaciar todo
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200 mb-5 overflow-x-auto pb-px">
        <TrashTab
          label="Todos"
          count={totalCount}
          active={activeTab === 'all'}
          onClick={() => setActiveTab('all')}
          testId="trash-tab-all"
        />
        {Object.entries(COLLECTION_META).map(([key, meta]) => (
          <TrashTab
            key={key}
            label={meta.label}
            icon={meta.icon}
            count={data.counts?.[key] || 0}
            active={activeTab === key}
            onClick={() => setActiveTab(key)}
            testId={`trash-tab-${key}`}
          />
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por título…"
          data-testid="trash-search-input"
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500 outline-none"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-charcoal-400">
          <Loader2 size={28} className="animate-spin mx-auto mb-2" />
          Cargando…
        </div>
      ) : filteredItems.length === 0 ? (
        <EmptyState activeTab={activeTab} hasAny={totalCount > 0} />
      ) : (
        <div className="space-y-2" data-testid="trash-list">
          {filteredItems.map((it) => (
            <TrashRow
              key={it.id}
              item={it}
              busy={busyId === it.id}
              onRestore={() => restoreItem(it.id, it.title)}
              onPurge={() => purgeItem(it.id, it.title)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TrashTab({ label, icon: Icon, count, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
        active
          ? 'text-cmic-600 border-cmic-500'
          : 'text-charcoal-500 hover:text-charcoal-800 border-transparent'
      }`}
    >
      {Icon ? <Icon size={14} /> : null}
      {label}
      {count > 0 && (
        <span className={`text-[11px] font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1.5 ${
          active ? 'bg-cmic-500 text-white' : 'bg-gray-100 text-charcoal-600'
        }`}>{count}</span>
      )}
    </button>
  );
}

function TrashRow({ item, busy, onRestore, onPurge }) {
  const meta = COLLECTION_META[item.collection] || { label: item.collection, icon: FileText, colorClasses: 'bg-gray-50 text-gray-600' };
  const Icon = meta.icon;
  const days = daysUntilPurge(item.purge_after);
  const purgeSoon = days !== null && days <= 7;
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between gap-4 hover:border-gray-300 transition-colors" data-testid={`trash-row-${item.id}`}>
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${meta.colorClasses}`}>
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-charcoal-500 bg-gray-100 px-1.5 py-0.5 rounded">{meta.label}</span>
            {purgeSoon && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                <AlertTriangle size={10} /> Se purga en {days} día{days === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-charcoal-800 mt-0.5 truncate" title={item.title}>{item.title}</h3>
          <p className="text-xs text-charcoal-500 mt-0.5">
            Eliminado {timeAgo(item.deleted_at)}
            {item.deleted_by ? ` por ${item.deleted_by}` : ''}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onRestore}
          disabled={busy}
          data-testid={`trash-restore-${item.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-md hover:bg-emerald-100 disabled:opacity-60"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
          Restaurar
        </button>
        <button
          onClick={onPurge}
          disabled={busy}
          data-testid={`trash-purge-${item.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 px-2.5 py-1.5 rounded-md disabled:opacity-60"
          title="Eliminar permanentemente (no se puede deshacer)"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ activeTab, hasAny }) {
  return (
    <div className="text-center py-16 border border-dashed border-gray-200 rounded-xl bg-gray-50/40">
      <Trash2 size={36} className="mx-auto text-charcoal-300 mb-3" />
      <p className="text-sm text-charcoal-500">
        {activeTab === 'all' && !hasAny
          ? 'La papelera está vacía. Los elementos eliminados aparecerán aquí.'
          : 'No hay elementos en esta categoría.'}
      </p>
    </div>
  );
}
